import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { updateReservation, getUnits, addPayment } from "@/lib/api";

const emptyForm = {
  guest_name: "", guest_email: "", unit_id: "",
  check_in: "", check_out: "",
  accommodation_total: "", status: "booked",
  channel: "", commission_amount: "",
  extra_guests: 0, extra_guest_names: [],
  notes: "",
  dietary_notes: "",
  severe_allergy: false,
  // Prepayment recording (only POSTs a payment if amount > 0 on save).
  prepayment_amount: "",
  prepayment_method: "cash",
  prepayment_date: "",
};

export default function EditReservationDialog({ reservation, open, onClose, onSaved }) {
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) getUnits().then(setUnits).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open && reservation) {
      setForm({
        guest_name: reservation.guest_name || "",
        guest_email: reservation.guest_email || "",
        unit_id: reservation.unit_id || "",
        check_in: reservation.check_in || "",
        check_out: reservation.check_out || "",
        accommodation_total: String(reservation.accommodation_total ?? ""),
        status: reservation.status || "booked",
        channel: reservation.channel || "",
        commission_amount: String(reservation.commission_amount ?? ""),
        extra_guests: Math.max(0, reservation.extra_guests || 0),
        extra_guest_names: Array.isArray(reservation.extra_guest_names)
          ? [...reservation.extra_guest_names] : [],
        notes: reservation.notes || "",
        dietary_notes: reservation.dietary_notes || "",
        severe_allergy: !!reservation.severe_allergy,
        prepayment_amount: "",
        prepayment_method: "cash",
        prepayment_date: new Date().toISOString().slice(0, 10),
      });
    }
  }, [open, reservation]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const setExtra = (n) => {
    const count = Math.max(0, parseInt(n) || 0);
    setForm(f => {
      const names = Array.from({ length: count }, (_, i) => f.extra_guest_names[i] || "");
      return { ...f, extra_guests: count, extra_guest_names: names };
    });
  };

  const save = async () => {
    if (!form.guest_name.trim()) { toast.error("Guest name is required"); return; }
    if (form.check_out <= form.check_in) { toast.error("Check-out must be after check-in"); return; }
    setSaving(true);
    try {
      await updateReservation(reservation.id, {
        guest_name: form.guest_name.trim(),
        guest_email: form.guest_email.trim(),
        unit_id: form.unit_id,
        check_in: form.check_in,
        check_out: form.check_out,
        accommodation_total: parseFloat(form.accommodation_total) || 0,
        status: form.status,
        channel: form.channel.trim(),
        commission_amount: parseFloat(form.commission_amount) || 0,
        extra_guests: form.extra_guests,
        extra_guest_names: (form.extra_guest_names || [])
          .slice(0, form.extra_guests)
          .map(n => (n || "").trim())
          .filter(Boolean),
        notes: form.notes,
        dietary_notes: (form.dietary_notes || "").trim(),
        severe_allergy: !!form.severe_allergy,
      });
      // Optionally record a prepayment alongside the edit. We POST a new payment
      // rather than mutating an existing one — this keeps the audit trail clean
      // and lets the drawer surface every prepayment as a separate row.
      const prepayAmt = parseFloat(form.prepayment_amount) || 0;
      if (prepayAmt > 0) {
        await addPayment({
          reservation_id: reservation.id,
          amount: prepayAmt,
          method: form.prepayment_method || "cash",
          date: form.prepayment_date || undefined,
        });
        toast.success(`Reservation updated · prepayment €${prepayAmt.toFixed(2)} recorded`);
      } else {
        toast.success("Reservation updated");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to update reservation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="edit-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Reservation</DialogTitle>
          <DialogDescription className="sr-only">Update reservation details.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Guest name</Label>
              <Input value={form.guest_name} onChange={(e) => upd("guest_name", e.target.value)} className="mt-1 h-11" data-testid="edit-guest-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input type="email" value={form.guest_email} onChange={(e) => upd("guest_email", e.target.value)} className="mt-1 h-11" data-testid="edit-guest-email" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Unit</Label>
            <Select value={form.unit_id} onValueChange={(v) => upd("unit_id", v)}>
              <SelectTrigger className="mt-1 h-11" data-testid="edit-unit">
                <SelectValue placeholder="Select unit" />
              </SelectTrigger>
              <SelectContent>
                {units.map(u => <SelectItem key={u.id} value={u.id}>{u.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Check-in</Label>
              <Input type="date" value={form.check_in} onChange={(e) => upd("check_in", e.target.value)} className="mt-1 h-11" data-testid="edit-checkin" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Check-out</Label>
              <Input type="date" value={form.check_out} onChange={(e) => upd("check_out", e.target.value)} className="mt-1 h-11" data-testid="edit-checkout" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Accommodation (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.accommodation_total} onChange={(e) => upd("accommodation_total", e.target.value)} className="mt-1 h-11" data-testid="edit-accommodation" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                <SelectTrigger className="mt-1 h-11" data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="checked_in">Checked in</SelectItem>
                  <SelectItem value="checked_out">Checked out</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Channel</Label>
              <Input value={form.channel} onChange={(e) => upd("channel", e.target.value)} className="mt-1 h-11" placeholder="Direct, Booking.com, Email..." list="edit-channel-suggestions" data-testid="edit-channel" />
              <datalist id="edit-channel-suggestions">
                <option value="Direct" /><option value="Booking.com" /><option value="Airbnb" />
                <option value="Expedia" /><option value="Hostelworld" /><option value="Email" />
                <option value="Phone" /><option value="Walk-in" />
              </datalist>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Commission (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.commission_amount} onChange={(e) => upd("commission_amount", e.target.value)} className="mt-1 h-11" placeholder="0.00" data-testid="edit-commission" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Additional guests</Label>
            <Input
              type="number" min="0" step="1"
              value={form.extra_guests}
              onChange={(e) => setExtra(e.target.value)}
              className="mt-1 h-11"
              data-testid="edit-extra-guests"
            />
            <div className="text-[11px] text-slate-400 mt-1">
              Total in the room: {1 + (form.extra_guests || 0)} guest{(1 + (form.extra_guests || 0)) === 1 ? "" : "s"}
            </div>
            {form.extra_guests > 0 && (
              <div className="mt-3 space-y-2" data-testid="edit-extra-guest-names">
                {Array.from({ length: form.extra_guests }).map((_, i) => (
                  <Input
                    key={i}
                    value={form.extra_guest_names[i] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm(f => {
                        const arr = [...f.extra_guest_names];
                        arr[i] = v;
                        return { ...f, extra_guest_names: arr };
                      });
                    }}
                    placeholder={`Additional guest ${i + 1} name`}
                    className="h-10"
                    data-testid={`edit-extra-guest-name-${i}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-3" data-testid="edit-prepayment-section">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Record a prepayment <span className="text-slate-300 normal-case tracking-normal">(optional)</span></div>
              <div className="text-[11px] text-slate-400 mt-0.5">Use this if the guest paid before booking and it wasn’t recorded yet. Leave the amount blank to skip.</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.prepayment_amount}
                  onChange={(e) => upd("prepayment_amount", e.target.value)}
                  className="mt-1 h-10"
                  placeholder="0.00"
                  data-testid="edit-prepayment-amount"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Method</Label>
                <Select
                  value={form.prepayment_method}
                  onValueChange={(v) => upd("prepayment_method", v)}
                >
                  <SelectTrigger className="mt-1 h-10" data-testid="edit-prepayment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Credit Card</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={form.prepayment_date}
                  onChange={(e) => upd("prepayment_date", e.target.value)}
                  className="mt-1 h-10"
                  data-testid="edit-prepayment-date"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="mt-1" rows={3} data-testid="edit-notes" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Dietary requirements <span className="text-slate-300 normal-case tracking-normal">(kitchen)</span></Label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.severe_allergy}
                  onChange={(e) => upd("severe_allergy", e.target.checked)}
                  className="accent-red-500"
                  data-testid="edit-severe-allergy"
                />
                <span>Severe allergy</span>
              </label>
            </div>
            <Input
              value={form.dietary_notes}
              onChange={(e) => upd("dietary_notes", e.target.value)}
              placeholder="e.g. Vegan, gluten free, nut allergy"
              className="mt-1 h-10"
              data-testid="edit-dietary-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-reservation-save-btn">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
